import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Confirm from 'components/Confirm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { bem } from 'helpers/class-name';

import messages from '../SkuMenuPage.messages';

const { block, element } = bem('delete-category-modal');

export const DELETE_CATEGORY_MODAL_NAME = 'DeleteCategoryModal';

const DeleteCategoryModal = ({ categoryName, movedItemNames, keptItemNames, onConfirmDelete }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCancel = () => {
    if (!isDeleting) {
      dispatch(closeModal());
    }
  };

  const handleConfirm = async () => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirmDelete?.();
      dispatch(closeModal());
    } catch (error) {
      // The caller handles user feedback; keep modal open so the action can be retried.
    } finally {
      setIsDeleting(false);
    }
  };

  const formatNames = names => intl.formatList(names, { type: 'conjunction' });
  const hasItems = movedItemNames.length > 0 || keptItemNames.length > 0;

  return (
    <div
      className={block()}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage(messages.deleteCategoryDialogLabel)}>
      <Confirm
        className={element('content')}
        title={
          <Message
            message={messages.deleteCategoryConfirmTitle}
            values={{ categoryName, strong: chunks => <strong>{chunks}</strong> }}
          />
        }
        cancelNode={<Message message={messages.deleteCategoryConfirmCancel} />}
        acceptNode={<Message message={messages.deleteCategoryConfirmAction} />}
        acceptDisabled={isDeleting}
        acceptWaiting={isDeleting}
        onCancel={handleCancel}
        onRequestClose={handleCancel}
        onConfirm={handleConfirm}>
        {hasItems ? (
          <>
            {movedItemNames.length > 0 && (
              <p className={element('line')}>
                <Message
                  message={messages.deleteCategoryMovedItems}
                  values={{ itemCount: movedItemNames.length, itemNames: formatNames(movedItemNames) }}
                />
              </p>
            )}
            {keptItemNames.length > 0 && (
              <p className={element('line')}>
                <Message
                  message={messages.deleteCategoryKeptItems}
                  values={{ itemCount: keptItemNames.length, itemNames: formatNames(keptItemNames) }}
                />
              </p>
            )}
            <p className={element('line')}>
              <Message message={messages.deleteCategoryUndone} />
            </p>
          </>
        ) : (
          <Message message={messages.deleteCategoryConfirmDescription} />
        )}
      </Confirm>
    </div>
  );
};

DeleteCategoryModal.propTypes = {
  categoryName: PropTypes.string,
  movedItemNames: PropTypes.arrayOf(PropTypes.string),
  keptItemNames: PropTypes.arrayOf(PropTypes.string),
  onConfirmDelete: PropTypes.func,
};

DeleteCategoryModal.defaultProps = {
  categoryName: '',
  movedItemNames: [],
  keptItemNames: [],
};

export default DeleteCategoryModal;
